import { useState, useContext, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Backdrop,
} from "@mui/material";
import { UserContext } from "../providers/userProvider";
import { useSearchParams } from "react-router-dom";

const API_BASE = "https://us-central1-whitesides-family-blog.cloudfunctions.net";

const STATUS_LABELS = {
  trial: { label: "Trial", color: "default" },
  active: { label: "Active", color: "success" },
  past_due: { label: "Past Due", color: "warning" },
  inactive: { label: "Inactive", color: "error" },
};

export const Settings = () => {
  const { user, setUser } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [fullName, setFullName] = useState(user.fullName || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);

  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [pendingPayees, setPendingPayees] = useState([]);
  const [addingPayee, setAddingPayee] = useState(false);
  const [releasingPayee, setReleasingPayee] = useState(null);
  const [releaseError, setReleaseError] = useState(null);

  const [billingLoading, setBillingLoading] = useState(false);
  const [checkoutMessage, setCheckoutMessage] = useState(null);
  const [billingError, setBillingError] = useState(null);

  useEffect(() => {
    if (user.groupId) fetchMembers();
  }, [user.groupId]);

  useEffect(() => {
    const status = searchParams.get("checkout");
    if (status === "success") {
      setCheckoutMessage({ type: "success", text: "Billing set up successfully! Your subscription is now active." });
      setSearchParams({});
    } else if (status === "cancelled") {
      setCheckoutMessage({ type: "warning", text: "Billing setup was cancelled." });
      setSearchParams({});
    }
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/getGroupMembers?groupId=${user.groupId}`);
      setMembers(res.data.members);
    } catch (e) {
      console.error(e);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.post(`${API_BASE}/updateProfile`, { userId: user.id, fullName });
      setUser((prev) => ({ ...prev, fullName }));
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleReleasePayee = async (targetUserId) => {
    setReleasingPayee(targetUserId);
    setReleaseError(null);
    try {
      await axios.post(`${API_BASE}/updatePayerFor`, {
        requesterId: user.id,
        targetUserId,
        newPayerId: targetUserId,
      });
      setMembers((prev) =>
        prev.map((m) => (m.id === targetUserId ? { ...m, paidBy: targetUserId } : m))
      );
    } catch (e) {
      console.error(e);
      setReleaseError(e.response?.data?.error || "Failed to remove member. Please try again.");
    } finally {
      setReleasingPayee(null);
    }
  };

  const handleSelectPending = (memberId) => {
    if (!pendingPayees.includes(memberId)) {
      setPendingPayees((prev) => [...prev, memberId]);
    }
  };

  const handleRemovePending = (memberId) => {
    setPendingPayees((prev) => prev.filter((id) => id !== memberId));
  };

  const handleConfirmTakeover = async () => {
    setAddingPayee(true);
    setBillingError(null);
    try {
      await Promise.all(
        pendingPayees.map((targetUserId) =>
          axios.post(`${API_BASE}/updatePayerFor`, {
            requesterId: user.id,
            targetUserId,
            newPayerId: user.id,
          })
        )
      );
      setMembers((prev) =>
        prev.map((m) => pendingPayees.includes(m.id) ? { ...m, paidBy: user.id } : m)
      );
      setPendingPayees([]);
    } catch (e) {
      console.error(e);
      setBillingError(e.response?.data?.error || "Failed to update billing. Please try again.");
    } finally {
      setAddingPayee(false);
    }
  };

  const handleSetUpBilling = async () => {
    setBillingLoading(true);
    setBillingError(null);
    try {
      const returnUrl = window.location.href.split("?")[0];
      const res = await axios.post(`${API_BASE}/createCheckoutSession`, { userId: user.id, returnUrl });
      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      setBillingError(e.response?.data?.error || "Something went wrong. Please try again.");
      setBillingLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    setBillingError(null);
    try {
      const returnUrl = window.location.href.split("?")[0];
      const res = await axios.post(`${API_BASE}/createBillingPortalSession`, { userId: user.id, returnUrl });
      window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      setBillingError(e.response?.data?.error || "Something went wrong. Please try again.");
      setBillingLoading(false);
    }
  };

  const myPayees = members.filter((m) => m.paidBy === user.id);
  const available = members.filter(
    (m) => m.paidBy !== user.id && m.id !== user.id && !pendingPayees.includes(m.id)
  );
  const payer = user.paidBy && user.paidBy !== user.id
    ? members.find((m) => m.id === user.paidBy)
    : null;
  const effectiveStatus = payer ? (payer.subscriptionStatus || "trial") : (user.subscriptionStatus || "trial");
  const statusInfo = STATUS_LABELS[effectiveStatus] || STATUS_LABELS.trial;

  const displayName = (m) =>
    m.id === user.id ? "You" : m.fullName || `***${m.phoneLast4}`;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Settings
      </Typography>

      {/* ── Profile ── */}
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        Profile
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Full Name"
          variant="outlined"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          fullWidth
        />
        <TextField
          label="Phone Number"
          variant="outlined"
          value={user.phoneNumber || ""}
          disabled
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleSaveProfile}
          disabled={savingProfile || fullName === user.fullName}
          sx={{ alignSelf: "flex-start" }}
        >
          {savingProfile ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
        {profileSuccess && <Alert severity="success">Profile updated.</Alert>}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* ── Billing ── */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Billing
      </Typography>

      {checkoutMessage && (
        <Alert severity={checkoutMessage.type} sx={{ mb: 2 }}>
          {checkoutMessage.text}
        </Alert>
      )}
      <Box sx={{ position: "relative" }}>
        <Backdrop
          open={membersLoading}
          sx={{
            position: "absolute",
            zIndex: 1,
            borderRadius: 1,
            backgroundColor: "rgba(255,255,255,0.7)",
            color: "#000",
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

        {/* Status */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">Subscription status:</Typography>
            <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
          </Box>
          {payer && (
            <Typography variant="body2" color="text.secondary">
              Subscription paid for by: <strong>{payer.fullName || `***${payer.phoneLast4}`}</strong>
            </Typography>
          )}
        </Box>

        {/* Who you're paying for */}
        {membersLoading ? (
          <Box sx={{ minHeight: 80 }} />
        ) : (
          <>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You are paying for:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {myPayees.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nobody yet.
                  </Typography>
                ) : (
                  myPayees.map((m) => (
                    <Chip
                      key={m.id}
                      label={displayName(m)}
                      variant="outlined"
                      onDelete={m.id !== user.id ? () => handleReleasePayee(m.id) : undefined}
                      disabled={releasingPayee === m.id}
                      icon={releasingPayee === m.id ? <CircularProgress size={14} /> : undefined}
                    />
                  ))
                )}
              </Box>
              {releaseError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setReleaseError(null)}>
                  {releaseError}
                </Alert>
              )}
            </Box>

            {/* Add someone to pay for */}
            {(available.length > 0 || pendingPayees.length > 0) && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Also pay for:
                </Typography>

                {/* Pending chips */}
                {pendingPayees.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                    {pendingPayees.map((id) => {
                      const m = members.find((m) => m.id === id);
                      return (
                        <Chip
                          key={id}
                          label={m ? m.fullName || `***${m.phoneLast4}` : id}
                          onDelete={() => handleRemovePending(id)}
                        />
                      );
                    })}
                  </Box>
                )}

                {available.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 220 }} disabled={addingPayee}>
                    <InputLabel sx={{ color: "text.primary", "&.Mui-focused": { color: "#000" } }}>Add a member...</InputLabel>
                    <Select
                      value=""
                      label="Add a member..."
                      onChange={(e) => handleSelectPending(e.target.value)}
                      sx={{
                        color: "text.primary",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.4)" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.8)" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                        "& .MuiSvgIcon-root": { color: "#000" },
                      }}
                    >
                      {available.map((m) => (
                        <MenuItem key={m.id} value={m.id}>
                          {m.fullName || `***${m.phoneLast4}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {pendingPayees.length > 0 && (
                  <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={handleConfirmTakeover}
                        disabled={addingPayee}
                      >
                        {addingPayee
                          ? <CircularProgress size={18} color="inherit" />
                          : "Take over billing for these members"}
                      </Button>
                    </Box>
                    {billingError && (
                      <Alert severity="error" onClose={() => setBillingError(null)}>
                        {billingError}
                      </Alert>
                    )}
                  </Box>
                )}

              </Box>
            )}
          </>
        )}

        {/* Billing action button */}
        {effectiveStatus === "active" ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              ${myPayees.length}/month for {myPayees.length} member{myPayees.length !== 1 ? "s" : ""}.
            </Typography>
            <Button
              variant="contained"
              onClick={handleManageBilling}
              disabled={billingLoading}
            >
              {billingLoading ? <CircularProgress size={20} color="inherit" /> : "Manage Billing"}
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {effectiveStatus === "trial"
                ? `You're on a free trial. Set up billing to keep access after your trial ends. You'll be charged $${myPayees.length}/month for ${myPayees.length} member${myPayees.length !== 1 ? "s" : ""}.`
                : effectiveStatus === "past_due"
                ? "Your last payment failed. Please update your payment method."
                : "Your subscription is inactive. Re-subscribe to restore access."}
            </Typography>
            <Button
              variant="contained"
              onClick={effectiveStatus === "past_due" ? handleManageBilling : handleSetUpBilling}
              disabled={billingLoading}
            >
              {billingLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : user.subscriptionStatus === "past_due" ? (
                "Update Payment Method"
              ) : (
                "Set Up Billing"
              )}
            </Button>
          </Box>
        )}
      </Box>
      </Box>
    </Container>
  );
};
